import { useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Building2, Bell, Users } from "lucide-react";
import NotificationsSection from "@/components/settings/NotificationsSection";
import TeamSection from "@/components/settings/TeamSection";

const SECTIONS = [
    { key: "workspace", label: "Workspace", icon: Building2 },
    { key: "notifications", label: "Notifications", icon: Bell },
    { key: "team", label: "Team", icon: Users },
];

export default function Settings() {
    const [section, setSection] = useState("workspace");

    return (
        <AppShell breadcrumbs={["Settings"]}>
            <div className="max-w-5xl">
                <div className="mb-8">
                    <p className="overline mb-2">Settings</p>
                    <h1 className="font-display text-4xl tracking-tight text-slate-900">Your workspace</h1>
                    <p className="text-slate-500 mt-2">Configure how your workspace communicates with clients.</p>
                </div>

                <div className="grid grid-cols-12 gap-8">
                    <aside className="col-span-12 sm:col-span-3" data-testid="settings-sidebar">
                        <nav className="rounded-xl border border-slate-200 bg-white p-2">
                            {SECTIONS.map((s) => {
                                const Icon = s.icon;
                                const active = section === s.key;
                                return (
                                    <button
                                        key={s.key}
                                        data-testid={`settings-nav-${s.key}`}
                                        onClick={() => setSection(s.key)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition ${active ? "bg-[#C55A46]/10 text-[#8B3A2A] font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                                    >
                                        <Icon className="w-4 h-4" /> {s.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </aside>

                    <section className="col-span-12 sm:col-span-9">
                        {section === "workspace" && <WorkspaceSection />}
                        {section === "notifications" && <NotificationsSection />}
                        {section === "team" && <TeamSection />}
                    </section>
                </div>
            </div>
        </AppShell>
    );
}

function WorkspaceSection() {
    const { user, workspace, refresh } = useAuth();
    const isOwner = user?.role === "owner";
    const [form, setForm] = useState({
        company_name: workspace?.company_name || "",
        website: workspace?.website || "",
        brand_color: workspace?.brand_color || "#C55A46",
    });
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch("/workspace", form);
            await refresh();
            toast.success("Workspace updated");
        } catch (err) {
            toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="rounded-xl border border-slate-200 bg-white p-8 mb-4" data-testid="settings-workspace-panel">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display text-xl text-slate-900">Workspace</h2>
                    {!isOwner && (
                        <span data-testid="settings-workspace-readonly-badge"
                            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                            Owner only · read-only
                        </span>
                    )}
                </div>
                <form onSubmit={save} className="space-y-5">
                    <div className="space-y-2">
                        <Label htmlFor="ws-name" className="text-slate-700">Company name</Label>
                        <Input id="ws-name" data-testid="settings-company"
                            value={form.company_name}
                            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                            disabled={!isOwner}
                            className="ring-terra disabled:bg-slate-50 disabled:text-slate-500" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="ws-web" className="text-slate-700">Website</Label>
                        <Input id="ws-web" placeholder="https://…" data-testid="settings-website"
                            value={form.website}
                            onChange={(e) => setForm({ ...form, website: e.target.value })}
                            disabled={!isOwner}
                            className="ring-terra disabled:bg-slate-50 disabled:text-slate-500" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="ws-color" className="text-slate-700">Brand accent</Label>
                        <div className="flex items-center gap-3">
                            <input id="ws-color" type="color" data-testid="settings-color"
                                value={form.brand_color}
                                onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                                disabled={!isOwner}
                                className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed" />
                            <span className="font-mono text-sm text-slate-600">{form.brand_color}</span>
                        </div>
                    </div>
                    {isOwner && (
                        <button type="submit" disabled={saving} data-testid="settings-save"
                            className="btn-terra px-4 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70">
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
                        </button>
                    )}
                </form>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-8">
                <h2 className="font-display text-xl text-slate-900 mb-6">Account</h2>
                <div className="space-y-3 text-sm">
                    <Row label="Name" value={user?.name} />
                    <Row label="Email" value={user?.email} />
                    <Row label="Role" value={user?.role} />
                    <Row label="Auth provider" value={user?.auth_provider} />
                </div>
            </div>
        </>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
            <span className="text-slate-500">{label}</span>
            <span className="text-slate-900 font-medium capitalize">{value || "—"}</span>
        </div>
    );
}
